import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment, Box, Typography, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ItemModal from './ItemModal';
import CustomerRateSlabModal from '../CustomerRateSlab/CustomerRateSlabModal';
import ExecutiveCommissionSlabModal from '../AccountHead/ExecutiveCommissionSlabModal';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import Chip from '@mui/material/Chip';

function ItemIndex() {
  // Session validation
  useSessionValidation();

  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, logout, token } = useAuth();
  const { logAudit } = useAuditLog();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedItem, setSelectedItem] = useState(null);

  // Dropdown data states
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [uoms, setUoms] = useState([]);

  // Vendor list modal states
  const [vendorListModalOpen, setVendorListModalOpen] = useState(false);
  const [vendorListItem, setVendorListItem] = useState(null);
  const [vendorListData, setVendorListData] = useState([]);
  const [vendorListLoading, setVendorListLoading] = useState(false);

  const handleOpenVendorList = async (item) => {
    setVendorListItem(item);
    setVendorListModalOpen(true);
    setVendorListLoading(true);
    try {
      const res = await fetchData('ItemSupplierRelationalApi', `/ItemSupplierDetailsList/${item.Id}`, token);
      const list = res?.response_data || res || [];
      setVendorListData(Array.isArray(list) ? list : []);
    } catch {
      setVendorListData([]);
    } finally {
      setVendorListLoading(false);
    }
  };
  const [slabModalOpen, setSlabModalOpen] = useState(false);
  const [slabItem, setSlabItem] = useState(null);
  const [masters, setMasters] = useState([]);

  // Commission slab modal states
  const [commissionSlabOpen, setCommissionSlabOpen] = useState(false);
  const [commissionSlabItem, setCommissionSlabItem] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return; // Prevent calls without required data

    setLoading(true);
    Promise.all([
      fetchtableData(),
      fetchCategoryData(),
      fetchBrandData(),
      fetchUOMData(),
      fetchMasters(),
      fetchVendors(),
      fetchBanks()
    ]).finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  const fetchtableData = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      console.log('Item data response:', response);

      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        console.log('Item data loaded successfully');
      } else {
        console.log('Item API failed or returned no data');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Item Details:', error);
      setData([]);
    }
  };

  const fetchCategoryData = async () => {
    try {
      // Fetch Categories with better error handling
      const categoryResponse = await fetchData('CategoryApi', `/${companyId}`, token);
      console.log('Category Response:', categoryResponse);

      if (categoryResponse && categoryResponse.responseCode === '1111' && categoryResponse.response_data) {
        setCategories(categoryResponse.response_data);
        console.log('Categories loaded successfully');
      } else {
        console.log('Category API failed or returned no data');
        setCategories([]);
      }

    } catch (error) {
      console.error('Error fetching category data:', error);
      setCategories([]);
    }
  };

  const fetchBrandData = async () => {
    try {
      const brandResponse = await fetchData('BrandApi', `/${companyId}`, token);
      console.log('Brand Response:', brandResponse);

      if (brandResponse && brandResponse.responseCode === '1111' && brandResponse.response_data) {
        setBrands(brandResponse.response_data);
        console.log('Brands loaded successfully');
      } else {
        console.log('Brand API failed or returned no data');
        setBrands([]);
      }
    } catch (error) {
      console.error('Error fetching brand data:', error);
      setBrands([]);
    }
  };

  const fetchUOMData = async () => {
    try {
      const uomResponse = await fetchData('UOMApi', `/${companyId}`, token);
      console.log('UOM Response:', uomResponse);

      if (uomResponse && uomResponse.responseCode === '1111' && uomResponse.response_data) {
        setUoms(uomResponse.response_data);
        console.log('UOMs loaded successfully');
      } else {
        console.log('UOM API failed or returned no data');
        setUoms([]);
      }
    } catch (error) {
      console.error('Error fetching UOM data:', error);
      setUoms([]);
    }
  };

  const [vendors, setVendors] = useState([]);
  const [banks, setBanks] = useState([]);

  const fetchMasters = async () => {
    try {
      const res = await fetchData('SalesExecutiveApi', `/${companyId}`, token);
      setMasters(res?.response_data || res || []);
    } catch {
      setMasters([]);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      setVendors(response.response_data || response || []);
    } catch {
      setVendors([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        console.log('Banks loaded:', response.response_data);
        setBanks(response.response_data);
      } else {
        setBanks([]);
      }
    } catch {
      setBanks([]);
    }
  };

  const handleDelete = async () => {
    deleteData('ItemApi', `${deleteItemId}`, token)
      .then((response) => {
        fetchtableData(); // Refresh data after deletion
        setDeleteItemId(null); // Reset the item ID
        setConfirmDialogOpen(false); // Close the dialog
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const handleActivate = (itemId) => {
    activateData('ItemApi', `${itemId}`, token)
      .then((response) => {
        fetchtableData(); // Refresh data after activation
      })
      .catch((error) => {
        // Handle errors for the activation request
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  // Modal handlers
  const handleOpenModal = async (mode, item = null) => {
    setModalMode(mode);
    if (mode === 'view') {
      const recordId = item?.Id || 0;
      logAudit(AUDIT_ACTION.VIEW, 'ProductItemApi', recordId);
    }
    if (item && item.Id && mode !== 'create') {
      try {
        const response = await fetchData('ItemApi', `/Item/${item.Id}`, token);
        const data = response?.response_data || response || item;
        // API returns ItemVM: { Item: {...}, OpeningBalances: [...] }
        // Flatten so ItemModal gets item fields + OpeningBalances together
        if (data?.Item) {
          setSelectedItem({
            ...data.Item,
            OpeningBalances: data.OpeningBalances || [],
            BundleRelations: data.ItemBundleRelation || [],
            ProductionRelations: data.ItemProductionRelation || [],
            ItemSpecs: data.ItemSpec || []
          });
        } else {
          setSelectedItem(data);
        }
      } catch {
        setSelectedItem(item);
      }
    } else {
      setSelectedItem(item);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleSaveItem = () => {
    fetchtableData(); // Refresh data
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter data based on search term
  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.Code?.toLowerCase().includes(searchLower) ||
      item.Name?.toLowerCase().includes(searchLower) ||
      item.CategoryName?.toLowerCase().includes(searchLower) ||
      item.HSNCode?.toLowerCase().includes(searchLower) ||
      item.GSTRate?.toString().includes(searchLower) ||
      item.MRP?.toString().includes(searchLower)
    );
  });

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Define table columns
  const columns = [
    { field: 'Sl.No', header: 'Sl.No', align: 'left' },
    // { field: 'Code', header: 'Item Code', align: 'left' },
    { field: 'Name', header: 'Item Name', align: 'left' },
    { field: 'CategoryName', header: 'Category', align: 'left' },
    { field: 'GSTRate', header: 'GST Rate (%)', align: 'right' },
    { field: 'HSNCode', header: 'HSN Code', align: 'left' },
    { field: 'MRP', header: 'MRP', align: 'right' },
    { field: 'IsInstallation', header: 'Installation Rq', align: 'left' },
  ];

  // Define action buttons for each row
  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>

      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          👁️
        </Button>
      </Tooltip>

      <Tooltip title="Edit Item" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('edit', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          ✏️
        </Button>
      </Tooltip>

      <Tooltip title={item.IsActive ? "Delete Item" : "Activate Item"} arrow>
        <Button
          size="small"
          onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: item.IsActive
              ? '0 1px 4px rgba(255, 107, 107, 0.3)'
              : '0 1px 4px rgba(78, 205, 196, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          {item.IsActive ? '🗑️' : '✅'}
        </Button>
      </Tooltip>
    </div>
  );

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        {loading && <LogoLoader open={loading} message="Loading items..." />}

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section with Title */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Item</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Search Bar and Create Button in Same Row */}
          <div style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code, name, category, HSN code, GST rate, or MRP..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#667eea', fontSize: '20px' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '13px',
                  height: '29px',
                  '&:hover fieldset': {
                    borderColor: '#667eea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            />
            <Tooltip title="Create a new item" arrow>
              <Button
                variant="contained"
                onClick={() => handleOpenModal('create')}
                className="action-button"
                style={{
                  background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '700',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
                  border: 'none',
                  minHeight: '29px',
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  whiteSpace: 'nowrap'
                }}
              >
                <span style={{ fontSize: '12px' }}>+</span>
                Create Item
              </Button>
            </Tooltip>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="item table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg,  #2C3E6E 0%, #2C3E6E 100%)',
                    height: '27px'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '4px 12px',
                        textAlign: column.align || 'left',
                        height: '27px'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '4px 12px',
                      textAlign: 'right',
                      height: '27px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {startIndex + index + 1}
                      </TableCell>
                      {/* <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.Code}
                      </TableCell> */}
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        <span
                          onClick={() => handleOpenVendorList(item)}
                          style={{ cursor: 'pointer', color: '#764ba2', fontWeight: '600', textDecoration: 'underline' }}
                        >
                          {item.Name}
                        </span>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.CategoryName || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right' }}>
                        {item.GSTRate}%
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.HSNCode}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'right' }}>
                        {item.MRP}
                      </TableCell>
                      <TableCell
                        style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          textAlign: 'left',
                        }}
                      >
                        <Chip
                          label={item.IsInstallation ? "Installation Required" : "Not Required"}
                          size="small"
                          sx={{
                            bgcolor: `${item.IsInstallation ? "#f0f1a3ff" : "#fee2e2"} !important`,
                            color: `${item.IsInstallation ? "#083519ff" : "#991b1b"} !important`,
                            fontWeight: 600,
                            fontSize: "11px",
                            borderRadius: "8px",
                          }}
                        />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this item record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Item Modal */}
        <ItemModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveItem}
          itemData={selectedItem}
          mode={modalMode}
          categories={categories}
          brands={brands}
          uoms={uoms}
          existingItems={data}
          vendors={vendors}
          banks={banks}
        />

        {/* Rate Slab Modal */}
        <CustomerRateSlabModal
          open={slabModalOpen}
          onClose={() => { setSlabModalOpen(false); setSlabItem(null); }}
          onSave={() => { }}
          item={slabItem}
          masters={masters}
        />

        {/* Executive Commission Slab Modal */}
        <ExecutiveCommissionSlabModal
          open={commissionSlabOpen}
          onClose={() => { setCommissionSlabOpen(false); setCommissionSlabItem(null); }}
          onSave={() => { }}
          item={commissionSlabItem}
          mode="item"
          masters={masters}
        />

        {/* Vendor List Modal */}
        <Dialog
          open={vendorListModalOpen}
          onClose={() => { setVendorListModalOpen(false); setVendorListItem(null); setVendorListData([]); }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ style: { borderRadius: '12px', background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)', padding: '2px' } }}
        >
          <Box style={{ background: 'white', borderRadius: '10px', overflow: 'hidden' }}>
            <DialogTitle style={{
              background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
              color: 'white', padding: '8px 12px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <Typography style={{ fontWeight: '600', fontSize: '14px' }}>
                Suppliers / Vendors — {vendorListItem?.Name}
              </Typography>
              <IconButton onClick={() => { setVendorListModalOpen(false); setVendorListItem(null); setVendorListData([]); }} style={{ color: 'white', padding: '2px' }} size="small">
                <CloseIcon style={{ fontSize: '18px' }} />
              </IconButton>
            </DialogTitle>
            <DialogContent style={{ padding: '12px 16px' }}>
              {vendorListLoading ? (
                <Typography style={{ textAlign: 'center', color: '#888', fontSize: '13px', padding: '20px' }}>Loading...</Typography>
              ) : vendorListData.length === 0 ? (
                <Typography style={{ textAlign: 'center', color: '#aaa', fontSize: '13px', padding: '20px' }}>No suppliers linked to this item.</Typography>
              ) : (
                <Table size="small" style={{ border: '1px solid #e9ecef', borderRadius: '6px', overflow: 'hidden' }}>
                  <TableHead>
                    <TableRow style={{ background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)' }}>
                      <TableCell style={{ color: 'white', fontSize: '11px', padding: '6px 10px', fontWeight: '600' }}>#</TableCell>
                      <TableCell style={{ color: 'white', fontSize: '11px', padding: '6px 10px', fontWeight: '600' }}>Supplier / Vendor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendorListData.map((v, i) => (
                      <TableRow key={v.Id || i} style={{ background: i % 2 === 0 ? '#f8f9fa' : 'white' }}>
                        <TableCell style={{ fontSize: '11px', padding: '5px 10px', color: '#555' }}>{i + 1}</TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '5px 10px', fontWeight: '500' }}>{v.MasterName || v.Name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DialogContent>
          </Box>
        </Dialog>

      </div>
    </Fade >
  );
}

export default ItemIndex;
